import { AppNode, GroupNode, NodeType } from "@features/builder/graph/types"
import { InternalNode } from "@xyflow/react"

type NodeId = AppNode["id"]

export type NodesGroupMapper<TNode extends AppNode | InternalNode<AppNode> = AppNode> = {
  /**  Parent group ID to first level of Sub-Groups ID's (shallow) mapping */
  parentToChildGroupsIdsMap: Map<NodeId, NodeId[]>
  /**  Parent group ID to Sub-Groups ID's (deep) mapping */
  parentToChildGroupsIdsMapDeep: Map<NodeId, NodeId[]>
  /**  Sub-Group ID to parent group ID (shallow) mapping */
  childToParentGroupsIdsMap: Map<NodeId, NodeId[]>
  /**  Sub-group ID to parents group IDs (deep) mapping */
  childToParentGroupsIdsMapDeep: Map<NodeId, NodeId[]>
  /** Group ID to first level of Nodes ID's (shallow) mapping */
  groupToNodesIdsMap: Map<NodeId, NodeId[]>
  /** Group ID to Nodes ID's (deep) mapping */
  groupToNodesIdsMapDeep: Map<NodeId, NodeId[]>
  /**  Node ID to not-group nodes mapping */
  nodesMap: Map<
    NodeId,
    Exclude<TNode, TNode extends InternalNode ? InternalNode<GroupNode> : GroupNode>
  >
  /** Group node ID to group node mapping */
  groupsMap: Map<NodeId, TNode extends InternalNode ? InternalNode<GroupNode> : GroupNode>
  /** Any node ID to group or non-group node mapping */
  allMap: Map<NodeId, TNode>
}

/**
 * Builds group-hierarchy maps: parent↔child group relations (shallow + deep),
 * group-to-node assignments, and node/group object lookups.
 * Node object maps (`allMap`, `nodesMap`, `groupsMap`) are populated from a `structuredClone` of the input;
 * ID-only maps are not deep-copied.
 */
export function nodesGroupMapper<TNode extends AppNode | InternalNode<AppNode>>(
  nodes: Iterable<TNode>,
): NodesGroupMapper<TNode> {
  const mapper: NodesGroupMapper<TNode> = {
    parentToChildGroupsIdsMap: new Map(),
    parentToChildGroupsIdsMapDeep: new Map(),
    childToParentGroupsIdsMap: new Map(),
    childToParentGroupsIdsMapDeep: new Map(),
    groupToNodesIdsMap: new Map(),
    groupToNodesIdsMapDeep: new Map(),
    nodesMap: new Map(),
    groupsMap: new Map(),
    allMap: new Map(),
  }

  const freezedNodes = structuredClone(nodes)

  for (const node of freezedNodes) {
    // fill allMap
    mapper.allMap.set(node.id, node)

    if (node.type === NodeType.Group) {
      // fill groupsMap
      mapper.groupsMap.set(
        node.id,
        node as unknown as TNode extends InternalNode ? InternalNode<GroupNode> : GroupNode,
      )
      // mapper.groupsMap.set(node.id, node as unknown as TNode extends InternalNode ? InternalNode<GroupNode> : GroupNode)

      if (node.parentId) {
        // first run for childParentGroupsIdsMap, map direct parent to every child
        if (mapper.childToParentGroupsIdsMap.has(node.id)) {
          mapper.childToParentGroupsIdsMap.get(node.id)?.push(node.parentId)
        } else {
          mapper.childToParentGroupsIdsMap.set(node.id ?? "", [node.parentId])
        }

        // first run for parentChildGroupsIdsMap, map direct child to every parent
        if (!mapper.parentToChildGroupsIdsMap.has(node.parentId)) {
          mapper.parentToChildGroupsIdsMap.set(node.parentId, [node.id])
        } else {
          mapper.parentToChildGroupsIdsMap.get(node.parentId)?.push(node.id)
        }
      }
    }

    if (node.type !== NodeType.Group) {
      // fill groupToNodesIdsMap
      if (node.parentId && mapper.groupToNodesIdsMap.has(node.parentId)) {
        mapper.groupToNodesIdsMap.get(node.parentId)?.push(node.id)
      } else {
        mapper.groupToNodesIdsMap.set(node.parentId ?? "", [node.id])
      }
      // fill nodesMap
      mapper.nodesMap.set(
        node.id,
        node as Exclude<TNode, TNode extends InternalNode ? InternalNode<GroupNode> : GroupNode>,
      )
    }
  }

  // @PERF (Bartek Laskowski) 2025-05-16: Deep run can be optimised by calling it on demand

  // final deep run for childToParentGroupsIdsMapDeep, fill all ancestors
  for (const childId of mapper.childToParentGroupsIdsMap.keys()) {
    mapper.childToParentGroupsIdsMapDeep.set(
      childId,
      getDeepChildParentsGroups(mapper.childToParentGroupsIdsMap, childId).parents,
    )
  }

  mapper.groupToNodesIdsMapDeep = new Map(mapper.groupToNodesIdsMap)
  // final deep run for parentChildGroupsIdsMapDeep, fill all ancestors
  for (const groupId of mapper.parentToChildGroupsIdsMap.keys()) {
    // Set deep subgroups
    const subGroups = getDeepNestedSubGroups(mapper.parentToChildGroupsIdsMap, groupId).subGroups
    mapper.parentToChildGroupsIdsMapDeep.set(groupId, subGroups)

    // Set all nodes from this group and all subgroups
    const allNodesIds = getDeepNestedNodes(mapper.groupToNodesIdsMap, groupId, subGroups)
    mapper.groupToNodesIdsMapDeep.set(groupId, allNodesIds)
  }

  return mapper
}

// @INFO (Bartek Laskowski) 2025-05-16: getDeepNestedSubGroups can do same thing, but let's keep them separately for clarity.
//                                      This fn can't do the same thing as getDeepNestedSubGroups
export function getDeepChildParentsGroups(
  childParentGroupsIdsMap: NodesGroupMapper["childToParentGroupsIdsMap"],
  subGroupId: string,
) {
  const subGroupWithParents = new Set(childParentGroupsIdsMap.get(subGroupId) ?? [])
  for (const parent of subGroupWithParents) {
    // childParentGroupsIdsMap starts with only one parent
    const parentIdOfChild: string | undefined = childParentGroupsIdsMap.get(parent)?.[0]
    if (parentIdOfChild) {
      // Add to Set being iterated to continue traversal up hierarchy
      // This extends the loop to process newly found parents
      subGroupWithParents.add(parentIdOfChild)
    }
  }

  return {
    subGroupId,
    parents: Array.from(subGroupWithParents),
  }
}

export function getDeepNestedSubGroups(
  parentChildGroupsIdsMap: NodesGroupMapper["parentToChildGroupsIdsMap"],
  groupId: string,
) {
  const subGroupWithParents = new Set(parentChildGroupsIdsMap.get(groupId) ?? [])
  for (const subGroup of subGroupWithParents) {
    for (const childIdOfParent of parentChildGroupsIdsMap.get(subGroup) ?? []) {
      if (childIdOfParent) {
        // Add to Set being iterated to continue traversal down hierarchy
        // This extends the loop to process newly found parents
        subGroupWithParents.add(childIdOfParent)
      }
    }
  }

  return {
    groupId,
    // @TODO (Bartek Laskowski) 2025-05-16: sort them by hierarchy. ATM they are sorted flat.
    subGroups: Array.from(subGroupWithParents),
  }
}

function getDeepNestedNodes(
  groupToNodesIdsMap: NodesGroupMapper["groupToNodesIdsMap"],
  groupId: NodeId,
  subGroups: NodeId[],
) {
  const allNodesIds: NodeId[] = [...(groupToNodesIdsMap.get(groupId) || [])]
  for (const subGroupId of subGroups) {
    const subGroupNodes = groupToNodesIdsMap.get(subGroupId) || []
    allNodesIds.push(...subGroupNodes)
  }
  return allNodesIds
}
